import { ColumnDef } from '@tanstack/react-table';
import { ExternalLinkIcon, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';

import { IAsset } from '@/types/asset.types';
import {
  chainLabel,
  explorerAddressUrl,
  truncateAddress,
} from '@/utils/chains';
import { complianceHealth, complianceLabel } from '@/utils/compliance';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import EditAssetDialog from './EditAssetDialog';

interface AssetsTableColumnsProps {
  refetchData: () => void;
}

export const AssetsTableColumns = ({
  refetchData,
}: AssetsTableColumnsProps): ColumnDef<IAsset>[] => [
  // {
  //   accessorKey: 'assetId',
  //   header: 'Asset ID',
  // },
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'type',
    header: 'Type',
  },
  {
    accessorKey: 'gold_weight',
    header: 'Gold Weight',
    cell: ({ row }) => {
      return `${row.original.gold_weight} oz`;
    },
  },
  {
    accessorKey: 'copper_weight',
    header: 'Copper Weight',
    cell: ({ row }) => {
      return `${row.original.copper_weight} lbs`;
    },
  },
  {
    accessorKey: 'chain',
    header: 'Chain',
    cell: ({ row }) => {
      // Labels rather than raw ids: an operator scanning this table wants "Avalanche Fuji",
      // not AVALANCHE_FUJI. An unmapped id falls through to the raw value so a newly added
      // chain shows as something rather than blank.
      const chain = row.original.chain;
      if (!chain) return 'N/A';
      return <Badge variant="outline">{chainLabel(chain)}</Badge>;
    },
  },
  {
    accessorKey: 'nav',
    header: 'NAV',
    cell: ({ row }) => {
      if (!row.original.nav) return 'N/A';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(row.original.nav);
    },
  },
  {
    accessorKey: 'erc3643',
    header: 'ERC-3643 Token',
    cell: ({ row }) => {
      const asset = row.original;
      const tokenAddress =
        asset.tokenRef ?? asset.erc3643?.tokenAddress ?? null;

      // The API builds the link, because it is the only side that knows which network this
      // asset is on. REACT_APP_BLOCKCHAIN_EXPLORER_URL is a single global value, so it sent
      // every token to the same explorer — correct while one chain issued tokens, and a
      // link to a nonexistent address the moment a second one does. The env var stays only
      // as a fallback for a backend that predates explorerUrl.
      const explorerUrl =
        asset.explorerUrl ??
        explorerAddressUrl(asset.chain, tokenAddress) ??
        (tokenAddress
          ? `${process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL}/address/${tokenAddress}`
          : null);

      return asset.erc3643 ? (
        <div>
          <div>{asset.erc3643.symbol}</div>
          {tokenAddress && (
            <div className="flex items-center gap-2">
              {`(${truncateAddress(asset.chain, tokenAddress)})`}{' '}
              {explorerUrl && (
                <a href={explorerUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLinkIcon size={16} className="text-blue-700" />
                </a>
              )}
            </div>
          )}
        </div>
      ) : (
        'N/A'
      );
    },
  },
  {
    id: 'aceCompliance',
    header: 'Compliance',
    cell: ({ row }) => {
      const badge = complianceBadge(row.original);
      if (!row.original.requireAccreditedInvestor) return badge;
      // Accredited-only shown beside the gate rather than folded into it: it is a second
      // policy on the same ACE target, not a different compliance mode.
      return (
        <div className="flex flex-col items-start gap-1">
          {badge}
          <Badge
            variant="outline"
            title="Only wallets with an accredited investor credential can hold or transfer this token."
          >
            Accredited only
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: 'isActive',
    header: 'Status',
    cell: ({ row }) => {
      const isActive = row.original.isActive;
      return (
        <Badge variant={isActive ? 'default' : 'secondary'}>
          {isActive ? 'Active' : 'Inactive'}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'nftTokenId',
    header: 'NFT Token ID',
    cell: ({ row }) => {
      const nftTokenId = row.original.nftTokenId || 'N/A';
      return (
        <span className="block truncate max-w-[200px]" title={nftTokenId}>
          {nftTokenId}
        </span>
      );
    },
  },
  // {
  //   accessorKey: 'createdAt',
  //   header: 'Created At',
  //   cell: ({ row }) => {
  //     return format(new Date(row.original.createdAt), 'MMM dd, yyyy');
  //   },
  // },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2">
          <EditAssetDialog asset={row.original} refetchData={refetchData} />
          <Link to={`/assets/${row.original.id}`}>
            <Button variant="outline" size="sm">
              <Eye size={16} className="mr-2" />
              View
            </Button>
          </Link>
        </div>
      );
    },
  },
];

/** The compliance-gate badge: requested mode vs what the chain enforces. */
function complianceBadge(asset: IAsset) {
  const erc3643 = asset.erc3643;
  if (!erc3643) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  const compliance = erc3643.compliance;

  // No compliance data at all: either a pre-ACE asset or an older API response. Not the
  // same as "broken", so it must not be coloured as a failure.
  if (!compliance) {
    return (
      <Badge variant="secondary" title="No compliance data for this token">
        Unknown
      </Badge>
    );
  }

  // Health is intent vs enforcement, NOT "is it ACE".
  //
  // This column used to treat engine === 'ACE_ONLY' as the definition of healthy, which
  // was correct while every asset was ACE and became actively misleading the moment one
  // was not: a legacy asset enforces LEGACY by design and would have been flagged red.
  const health = complianceHealth(compliance);
  const requested = compliance.requestedEngine ?? compliance.engine;
  const mode = complianceLabel(requested);
  const methods = compliance.protectedMethods ?? [];

  if (health === 'BROKEN') {
    return (
      <Badge
        variant="destructive"
        title={`Issued as ${mode} but the chain is enforcing ${complianceLabel(compliance.engine)}. Every transfer and mint on this token reverts.`}
      >
        {mode}: not enforced
      </Badge>
    );
  }

  if (health === 'PROVISIONING') {
    return (
      <Badge
        variant="secondary"
        className="bg-amber-100 text-amber-800 hover:bg-amber-100"
        title={`ACE target is ${compliance.aceTargetStatus ?? 'not registered'}. Policies may not be live on-chain yet.`}
      >
        {mode}: pending
      </Badge>
    );
  }

  if (health === 'UNKNOWN') {
    return (
      <Badge variant="secondary" title="Compliance state not reported">
        Unknown
      </Badge>
    );
  }

  // In sync. For an ACE asset the target must also be live on-chain before transfers are
  // genuinely gated; a legacy asset has no target, so its absence is not a fault.
  const needsTarget = requested !== 'LEGACY';
  if (needsTarget && compliance.aceTargetStatus !== 'ACTIVE') {
    return (
      <Badge
        variant="secondary"
        className="bg-amber-100 text-amber-800 hover:bg-amber-100"
        title={`ACE target is ${compliance.aceTargetStatus ?? 'not registered'}. Policies may not be live on-chain yet.`}
      >
        {mode}: {compliance.aceTargetStatus === 'FAILED' ? 'failed' : 'pending'}
      </Badge>
    );
  }

  return (
    <Badge
      className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
      title={
        needsTarget
          ? `Gated methods: ${methods.length ? methods.join(', ') : 'none'}`
          : 'Gated by the per-asset ERC-3643 identity registry.'
      }
    >
      {needsTarget ? `${mode} (${methods.length})` : mode}
    </Badge>
  );
}
